import { merge } from 'lodash-es';

import type { AnnotationsConfigProperties } from '../../core/components/annotations/types/annotations-config-properties.js';
import { getExportableAnnotationsConfig } from '../../core/components/annotations/utils/get-exportable-annotations-config.js';
import { mergeAnnotationsConfig } from '../../core/components/annotations/utils/merge-annotations-config.js';
import {
  DEFAULT_INFINITE_ZOOM,
  DEFAULT_TRUNCATION_MODE,
  DEFAULT_VALUE_REPRESENTATION,
} from '../../core/constants.js';
import { isValidColorRule } from '../../core/slots/ColorRule.js';
import {
  ColorPalette,
  CustomColorPalette,
  isCustomColorPalette,
} from '../../core/types/color-palette.js';
import type { ValueRepresentation } from '../../core/types/scales.js';
import { isValidCSSColor } from '../../core/utils/getValidColor.js';
import { getInteractions } from '../../core/utils/interactions.js';
import { isValidColorPalette } from '../../core/utils/is-valid-color-palette.js';
import { isValidLegendPosition } from '../../core/utils/is-valid-legend-position.js';
import type { HistogramAnnotationsConfigProps } from '../components/annotations-histogram/types.js';
import {
  DEFAULT_SCALE_FALLBACK_COLOR,
  defaultColorPalette,
  defaultHistogramChartConfig,
  defaultLegend,
  defaultTooltipVariant,
  defaultXAxis,
  defaultYAxis,
} from '../constants/defaults.js';
import { HistogramThresholdIndicatorProps } from '../slots/ThresholdIndicator.js';
import {
  HistogramChartConfigProperties,
  HistogramChartConfigProps,
  HistogramChartConfiguration,
  HistogramChartLegendConfig,
  HistogramChartLegendProps,
  HistogramChartSlots,
  HistogramChartToolbarConfig,
  HistogramChartTooltipConfig,
  HistogramChartTooltipProps,
  HistogramChartXAxisConfig,
  HistogramChartXAxisProps,
  HistogramChartYAxisConfig,
  HistogramChartYAxisProps,
  HistogramThresholdConfig,
  HistogramThresholdsConfig,
} from '../types/histogram-props.js';
import {
  HistogramTooltipVariant,
  histogramTooltipVariantOptions,
  type HistogramZoomDomain,
} from '../types/histogram.js';
import { getToolbarConfiguration } from './get-toolbar-configuration.js';

/**
 * Builds a ColorPalette configuration based on context and props
 * @param contextColorPalette - color palette context
 * @param propsColorPalette - color palette props
 */
export function getColorPaletteConfiguration(
  contextColorPalette?: ColorPalette | CustomColorPalette,
  propsColorPalette?: ColorPalette | CustomColorPalette,
) {
  const colorPalette =
    propsColorPalette ?? contextColorPalette ?? defaultColorPalette;

  return isCustomColorPalette(colorPalette) || isValidColorPalette(colorPalette)
    ? colorPalette
    : (defaultHistogramChartConfig.colorPalette ?? defaultColorPalette);
}

export function getLegendConfiguration(
  contextLegend: HistogramChartLegendProps | undefined,
  slotsLegend: HistogramChartLegendProps,
): HistogramChartLegendConfig {
  const legendConfig: HistogramChartLegendConfig = merge(
    {},
    defaultLegend,
    contextLegend,
    slotsLegend,
  );

  legendConfig.position = isValidLegendPosition(legendConfig.position)
    ? legendConfig.position
    : defaultLegend.position;

  return legendConfig;
}

/**
 * Returns true if tooltipVariant is a valid TooltipVariant
 * @param tooltipVariant - tooltip variant
 */
export const isValidHistogramTooltipVariant = (
  tooltipVariant: string,
): tooltipVariant is HistogramTooltipVariant =>
  (histogramTooltipVariantOptions as unknown as string[]).includes(
    tooltipVariant,
  );

export function getTooltipConfiguration(
  contextTooltip: HistogramChartTooltipProps | undefined,
  slotsTooltip: HistogramChartTooltipProps,
) {
  const tooltip = merge(
    {},
    { variant: defaultTooltipVariant },
    contextTooltip,
    slotsTooltip,
  ) as HistogramChartTooltipConfig;

  tooltip.variant = isValidHistogramTooltipVariant(tooltip.variant)
    ? tooltip.variant
    : defaultTooltipVariant;

  return tooltip;
}

export function getAnnotationsConfiguration(
  contextAnnotations: AnnotationsConfigProperties | undefined,
  slotsAnnotations: HistogramAnnotationsConfigProps | undefined,
): HistogramAnnotationsConfigProps | undefined {
  return mergeAnnotationsConfig(contextAnnotations, slotsAnnotations);
}

/**
 * Returns an array with the left and right axis config in that order
 * @param yAxes - YAxis config to be sanitized
 * @returns HistogramChartYAxisProps[]
 */
const sanitizeYAxisConfig = (
  yAxes: HistogramChartYAxisProps[],
): HistogramChartYAxisConfig => {
  const reversedYAxes = yAxes.reverse();
  const leftAxis = reversedYAxes.find(
    (axis) => axis?.position === 'left' || axis?.position === undefined,
  );
  const rightAxis = reversedYAxes.find((axis) => axis?.position === 'right');
  const sanitizedYAxes: HistogramChartYAxisConfig = {};

  if (leftAxis) {
    sanitizedYAxes.left = leftAxis;
  }
  if (rightAxis) {
    sanitizedYAxes.right = rightAxis;
  }

  return sanitizedYAxes;
};

/**
 * Builds a y-axis configuration based on slots and context
 * @param slotsYAxis - y axis slots
 * @param contextYAxis - contextXAxis
 */
export function getYAxisConfiguration(
  slotsYAxis: HistogramChartYAxisProps[],
  contextYAxis: HistogramChartYAxisProps[] | undefined,
): HistogramChartYAxisConfig {
  const contextConfig =
    contextYAxis === undefined ? {} : sanitizeYAxisConfig(contextYAxis);
  const slotsConfig =
    slotsYAxis === undefined ? {} : sanitizeYAxisConfig(slotsYAxis);

  const { left, right } = merge({}, contextConfig, slotsConfig);

  if (left === undefined && right === undefined) {
    return defaultYAxis;
  }

  const result: HistogramChartYAxisConfig = {};

  if (left) {
    result.left = merge({}, defaultYAxis.left, left);
  }

  if (right) {
    const defaultRight = { ...defaultYAxis.left, position: 'right' };
    result.right = merge({}, defaultRight, right);
  }

  return result;
}

/**
 * Builds an x-axis configuration based on context and slots
 * @param contextXAxis - x axis context
 * @param slotsXAxis - x axis slots
 */
export function getXAxisConfiguration(
  contextXAxis: HistogramChartXAxisProps | undefined,
  slotsXAxis: HistogramChartXAxisProps | undefined,
): HistogramChartXAxisConfig {
  return merge({}, defaultXAxis, contextXAxis, slotsXAxis);
}

/**
 * Builds thresholds configuration based on context and slots
 * @param contextThresholds - thresholds context
 * @param slotsThresholds - thresholds slots
 */
export function getThresholdsConfiguration(
  contextThresholds: HistogramThresholdConfig[] = [],
  slotsThresholds: HistogramThresholdIndicatorProps[] = [],
): HistogramThresholdsConfig | undefined {
  const allThresholds = contextThresholds
    .concat(slotsThresholds)
    .map((threshold) => ({
      ...threshold,
      id: crypto.randomUUID(),
      position: threshold.position ? threshold.position : 'left',
      color: isValidCSSColor(threshold.color)
        ? threshold.color
        : DEFAULT_SCALE_FALLBACK_COLOR,
    }));

  if (!allThresholds.length) {
    return undefined;
  }

  const thresholdConfig: HistogramThresholdsConfig = {};

  thresholdConfig.leftThreshold = allThresholds.filter(
    (threshold) => threshold.position === 'left',
  );

  thresholdConfig.rightThreshold = allThresholds.filter(
    (threshold) => threshold.position === 'right',
  );

  return thresholdConfig;
}

const getValueRepresentation = (
  contextValueRepresentation?: ValueRepresentation,
  configValueRepresentation?: ValueRepresentation,
) =>
  contextValueRepresentation ??
  configValueRepresentation ??
  DEFAULT_VALUE_REPRESENTATION;

/**
 * Builds infiniteZoom configuration based on context and props
 * @param contextConfig -
 * @param propsConfig -
 */
export function getInfiniteZoomConfiguration(
  contextConfig?: boolean,
  propsConfig?: boolean,
) {
  return propsConfig ?? contextConfig ?? DEFAULT_INFINITE_ZOOM;
}

/**
 * Builds initialZoom configuration based on context and props
 * @param contextConfig -
 * @param propsConfig -
 */
export function getInitialZoomConfiguration(
  contextConfig?: HistogramZoomDomain,
  propsConfig?: HistogramZoomDomain,
) {
  return propsConfig ?? contextConfig;
}

/**
 * Builds currentZoom configuration based on context and props
 * @param contextConfig -
 * @param propsConfig -
 */
export function getCurrentZoomConfiguration(
  contextConfig?: HistogramZoomDomain,
  propsConfig?: HistogramZoomDomain,
) {
  return propsConfig ?? contextConfig;
}

/**
 * Creates a Histogram chart configuration merging the configuration
 * provided in the context config, slots, and defaults.
 * @param slots - histogram chart slots
 * @param contextConfig - histogram chart context
 * @param histogramChartConfigProps - histogram chart props
 */
export function createHistogramConfiguration(
  slots: HistogramChartSlots,
  contextConfig: HistogramChartConfigProperties,
  histogramChartConfigProps: HistogramChartConfigProps,
): HistogramChartConfiguration {
  const colorPalette = getColorPaletteConfiguration(
    contextConfig.colorPalette,
    histogramChartConfigProps?.colorPalette,
  );
  const legend = getLegendConfiguration(contextConfig.legend, slots.legend);
  const tooltip = getTooltipConfiguration(contextConfig.tooltip, slots.tooltip);
  const yAxis = getYAxisConfiguration(slots.yAxis, contextConfig.yAxis);
  const xAxis = getXAxisConfiguration(contextConfig.xAxis, slots.xAxis);
  const truncationMode =
    histogramChartConfigProps?.truncationMode ??
    contextConfig?.truncationMode ??
    DEFAULT_TRUNCATION_MODE;

  const toolbar: HistogramChartToolbarConfig = getToolbarConfiguration(
    contextConfig.toolbar,
    slots.toolbar,
    slots.downloadCSV,
    contextConfig.downloadCSV,
  );
  const propsInteractions = {
    onZoomChange: histogramChartConfigProps.onZoomChange,
  };

  const hasContextInteractions =
    contextConfig && (contextConfig.pan || contextConfig.zoom);

  const chartConfigInteractions = hasContextInteractions
    ? {
        pan: { enabled: contextConfig.pan?.disabled !== true },
        zoom: { enabled: contextConfig.zoom?.disabled !== true },
        zoomX: { enabled: contextConfig.zoom?.disabled !== true },
      }
    : {};
  const interactions = getInteractions(
    contextConfig.interactions,
    slots.interactions ?? {},
    propsInteractions,
    chartConfigInteractions,
  );

  const configThresholds = contextConfig.thresholdIndicators || [];
  const thresholds = getThresholdsConfiguration(
    configThresholds,
    slots.thresholds,
  );

  const valueRepresentation = getValueRepresentation(
    contextConfig.valueRepresentation,
    histogramChartConfigProps.valueRepresentation,
  );

  const initialZoom = getInitialZoomConfiguration(
    contextConfig.initialZoom,
    histogramChartConfigProps.initialZoom,
  );

  const currentZoom = getCurrentZoomConfiguration(
    contextConfig.currentZoom,
    histogramChartConfigProps.currentZoom,
  );

  const infiniteZoom = getInfiniteZoomConfiguration(
    contextConfig.infiniteZoom,
    histogramChartConfigProps.infiniteZoom,
  );
  const colorRules = (contextConfig.colorRules || [])
    .filter(isValidColorRule)
    .concat(slots.colorRules || []);

  return {
    valueRepresentation,
    colorPalette,
    legend,
    tooltip,
    yAxis,
    xAxis,
    truncationMode,
    toolbar,
    annotations: getAnnotationsConfiguration(
      contextConfig.annotations,
      slots.annotations,
    ),
    interactions,
    thresholds,
    initialZoom,
    currentZoom,
    infiniteZoom,
    select: slots.select,
    colorRules,
  };
}

/**
 * Get chart configuration merging the default one with the stringify json config.
 * In case there is a conflict property, default would prevail.
 * In case there is any parse error. It will return an empty config.
 *
 * @param serializedConfig - stringify json config
 * @returns - Merged configuration
 */
export function deserializeConfig(
  serializedConfig: string,
): HistogramChartConfigProperties {
  try {
    const parsedData = JSON.parse(serializedConfig);
    parsedData.interactions = {
      ...parsedData.interactions,
      zoom: { enabled: parsedData.zoom?.disabled !== true },
      zoomX: { enabled: parsedData.zoom?.disabled !== true },
    };

    return merge({}, defaultHistogramChartConfig, parsedData);
  } catch {
    return defaultHistogramChartConfig;
  }
}

function parseYAxisConfig(yAxis: HistogramChartYAxisConfig) {
  const { left, right } = yAxis;

  const yAxisConfig: HistogramChartYAxisProps[] = [];

  if (left) {
    yAxisConfig.push(left);
  }
  if (right) {
    yAxisConfig.push(right);
  }

  return yAxisConfig;
}

function parseThresholdsConfig(
  thresholds: HistogramThresholdsConfig,
): HistogramThresholdIndicatorProps[] {
  const { leftThreshold, rightThreshold } = thresholds;

  const thresholdsConfig: HistogramThresholdIndicatorProps[] = [];
  if (leftThreshold) {
    thresholdsConfig.push(...leftThreshold);
  }
  if (rightThreshold) {
    thresholdsConfig.push(...rightThreshold);
  }

  return thresholdsConfig;
}

/**
 * Normalizes a final Histogram config to look like the configuration object.
 * @param config - histogram chart config
 * @returns parsed histogram config
 */
export function normalizeConfig(
  config: HistogramChartConfiguration,
): HistogramChartConfigProperties {
  const { thresholds, annotations, ...rest } = config;
  const thresholdIndicators = thresholds
    ? parseThresholdsConfig(thresholds)
    : undefined;

  const { intents, ...toolbar } = config.toolbar ?? {};

  return {
    ...rest,
    toolbar,
    yAxis: parseYAxisConfig(config.yAxis),
    thresholdIndicators,
    annotations: getExportableAnnotationsConfig(annotations),
  };
}
